import { AsyncAction } from "@/Common/Api/AsyncActions/AsyncAction";
import { Client } from "@/Common/Api/Client";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { PdfPageDetails } from "@/Common/Components/Pdf/PdfViewer/PdfViewer";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { Guid } from "@/Common/Models/Guid";
import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import { DocumentUIState, InboundFaxMailboxType, InboundFaxSortOrderType, SortDirection } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { Server } from "@/Common/Operations/operations.server";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { AttachFaxResponse } from "@/Documents/Api/Dto/Response/AttachFaxResponse";
import { CreatePatientResponse } from "@/Documents/Api/Dto/Response/CreatePatientResponse";
import { DownloadInboundFaxResponse } from "@/Documents/Api/Dto/Response/DownloadInboundFaxResponse";
import { LoadInboundFaxesResponse } from "@/Documents/Api/Dto/Response/LoadInboundFaxesResponse";
import { LoadInboundFaxPagesResponse } from "@/Documents/Api/Dto/Response/LoadInboundFaxPagesResponse";
import { InboundFax } from "@/Documents/Models/InboundFax/InboundFax";
import { InboundFaxDetails } from "@/Documents/Models/InboundFax/InboundFaxDetails";
import { InboundFaxIndexingFormModel } from "@/Documents/Models/InboundFax/InboundFaxIndexingFormModel";
import { InboundFaxMailboxModel } from "@/Documents/Models/InboundFax/InboundFaxMailboxModel";
import { InboundFaxPageModel } from "@/Documents/Models/InboundFax/InboundFaxPageModel";
import { InboundFaxMailboxFilterSettingsModel } from "@/Documents/Models/Settings/InboundFaxMailboxFilterSettings";
import { InboundFaxTriageFormSettingsModel } from "@/Documents/Models/Settings/InboundFaxTriageFormSettings";
import { InboundFaxView } from "@/Documents/Models/UI/InboundFaxEnums";
import { UserSettings } from "@/Globals/Models/UserSettings";

class InboundFaxesMailboxActions extends AsyncAction {
    private static actions = new InboundFaxesMailboxActions();

    public static async refreshFaxesList(mailboxModel: InboundFaxMailboxModel, mailboxType: InboundFaxMailboxType): Promise<void> {
        const response = await this.loadFaxes(mailboxModel, mailboxType);

        if (response.IsSuccess) {
            this.setLoadInboundFaxesResponse(response);
            InboundFaxesMailboxActions.updateInboundFaxUnmanagedFaxCount(documentsStore.InboundFaxInboxModel.filteringSettings);
        }
    }

    public static async loadNextPageAndReturnFirstFax(mailboxModel: InboundFaxMailboxModel, mailboxType: InboundFaxMailboxType): Promise<InboundFax> {
        return this.loadForPagination(mailboxModel, mailboxType, 1);
    }

    public static async loadPreviousPageAndReturnLastFax(mailboxModel: InboundFaxMailboxModel, mailboxType: InboundFaxMailboxType): Promise<InboundFax> {
        return this.loadForPagination(mailboxModel, mailboxType, -1);
    }

    public static async restoreFaxes(faxIds: string[]): Promise<void> {
        return this.actions.postAsync<null>(Server.Myle_Faxing_RestoreInboundFaxes, { FaxIds: faxIds });
    }

    public static async permanentlyDeleteFaxes(faxIds: string[]): Promise<void> {
        return this.actions.postAsync<null>(Server.Myle_Faxing_PermanentlyDeleteInboundFaxes, { FaxIds: faxIds });
    }

    public static async deleteFaxes(faxIds: string[]): Promise<void> {
        return this.actions.postAsync<null>(Server.Myle_Faxing_DeleteInboundFaxes, { FaxIds: faxIds });
    }

    public static async deleteSingleFax(faxId: string): Promise<void> {
        return this.actions.postAsync<null>(Server.Myle_Faxing_DeleteInboundFaxes, { FaxIds: [faxId] });
    }

    public static storeTriageFaxInfo(currentFaxFormData: InboundFaxIndexingFormModel, triageFaxesFormData: InboundFaxIndexingFormModel[]): void {
        const updatedData = [...triageFaxesFormData.filter(fax => fax.id !== currentFaxFormData.id), currentFaxFormData];
        this.dispatchLocal({ triageFaxes: updatedData });
    }

    public static removeTriageFaxInfo(inboundFaxId: string, triageFaxesFormData: InboundFaxIndexingFormModel[]): void {
        if (triageFaxesFormData?.any(x => x.id == inboundFaxId))
            this.dispatchLocal({ triageFaxes: triageFaxesFormData.filter(x => x.id !== inboundFaxId) });
    }

    public static clearTriageFaxInfos(): void {
        this.dispatchLocal({ triageFaxes: [] });
    }

    public static async returnToSender(faxIds: string[], faxDetails: InboundFaxDetails, selectedDocumentPages?: number[]): Promise<GenericResponse> {
        const { faxNumber, notes, phoneNumber, displayName, pdfCoverPageParams } = faxDetails;

        const response = await this.actions.postAsync<GenericResponse>(
            Server.Myle_Faxing_ReturnInboundFaxesToSender,
            {
                FaxIds: faxIds,
                Notes: notes,
                FaxNumber: faxNumber,
                RecipientPhoneNumber: phoneNumber,
                RecipientName: displayName,
                PdfCoverPageParams: pdfCoverPageParams,
                SelectedPages: selectedDocumentPages
            }
        );

        return response;
    }

    public static async sendFax(faxDetails: InboundFaxDetails, onCompleted?: VoidFunction, onPopupClose?: VoidFunction): Promise<void> {
        const { fileName, faxNumber, displayName, phoneNumber, pdfCoverPageParams } = faxDetails;

        const response = await Client.postAsync(
            ServerOperations.Server.Myle_Faxing_SendFaxRequest,
            {
                FileName: fileName,
                FaxNumber: faxNumber,
                PhoneNumber: phoneNumber,
                DisplayName: displayName,
                PdfCoverPageParams: pdfCoverPageParams
            }
        );

        if (response.errors.any()) {
            sessionStore.showPopup({
                content: response.errors,
                okAction: () => onCompleted?.()
            });
        }
        else if (response.isSuccess) {
            onPopupClose?.();
        }
    }

    public static async GeneratePdfPreview(inboundFaxId: Guid | string, pageNumbers: number[], mailboxType: InboundFaxMailboxType): Promise<void> {
        const docPath = apiSettings.buildAppUrl(`api/inboundfax/generatepdfpreview/${inboundFaxId}/${pageNumbers}/${mailboxType}`, { auth: true });
        MyleHelper.OpenNewWindow(docPath);
    }

    public static async attachFax(faxId: string, data: InboundFaxIndexingFormModel, selectedPages: PdfPageDetails[], isApproved: boolean = false): Promise<AttachFaxResponse> {
        return this.actions.postAsync<AttachFaxResponse>(

            Server.Myle_Faxing_AttachFaxToDocument,
            {
                faxId,
                source: data.source,
                description: data.description,
                notes: data.notes,
                documentDate: data.documentDate,
                selectedSiteCode: data.selectedSiteCode,
                isUrgent: data.isUrgent,
                selectedClassLookupId: data.selectedClassLookupId,
                selectedSubClassLookupId: data.selectedSubClassLookupId,
                selectedResponsibleProviderIds: data.selectedResponsibleProviders.map(x => x.ContactId),
                selectedPatientId: data.selectedPatient?.ContactId,
                selectedPages: selectedPages.map(x => x.pageNumber),
                isApproved: isApproved,
                shouldAssignClassFaxNumber: data.shouldAssignClassFaxNumber,
                shouldAssignSourceFaxNumber: data.shouldAssignSourceFaxNumber
            }
        );
    }

    public static async changeQueryPageNumber(pageNumber: number): Promise<void> {
        this.dispatchLocal({ queryCurrentPageNumber: pageNumber });
    }

    public static sortFaxList(queryOrderType: InboundFaxSortOrderType, queryOrderDirection: SortDirection): void {
        this.dispatchLocal({
            queryOrderType,
            queryOrderDirection
        });
    }

    public static async onSearchTextChange(searchText: string): Promise<void> {
        this.dispatchLocal({ searchText });
    }

    /**
     * update unmanaged fax count.
     * @param {InboundFaxMailboxFilterSettingsModel} filterSettings .
    */
    public static async updateInboundFaxUnmanagedFaxCount(filterSettings: InboundFaxMailboxFilterSettingsModel): Promise<void> {
        const unmanagedFaxes = await this.actions.postAsync<{ UnmanagedFaxCount: number, UrgentFaxCount: number, IsSuccess: boolean }>(
            ServerOperations.Server.Myle_Faxing_GetInboundFaxUnmanagedFaxCount,
            { filterSettings }
        );

        if (unmanagedFaxes?.IsSuccess) {
            this.dispatchLocal(
                {
                    queryRowCount: unmanagedFaxes.UnmanagedFaxCount,
                    urgentFaxesCount: unmanagedFaxes.UrgentFaxCount
                },
                DocumentUIState.InboundFaxInboxMailbox
            );

            const isClericalUser = sessionStore.getCurrentEmployee().isClerical();
            if (isClericalUser) {
                const documentTab = cacheStore.Tabs?.filter(tab => tab.key === MyleTabKey.Documents).firstOrDefault();
                if (documentTab && (documentTab.topCountUnmanagedFaxes !== unmanagedFaxes.UnmanagedFaxCount || documentTab.bottomCountUrgentUnmanagedFaxes !== unmanagedFaxes.UrgentFaxCount)) {
                    cacheStore.updateUnmanagedFaxesCounter(MyleTabKey.Documents, unmanagedFaxes.UnmanagedFaxCount, unmanagedFaxes.UrgentFaxCount);
                    cacheStore.EmitChangeToListeners();
                }
            }
        }
    }

    public static async ensureFaxIsMarkedAsRead(faxToMarkAsUnread: InboundFax, faxes: InboundFax[]): Promise<GenericResponse | null> {
        return await this.ensureFaxesAreMarkedAsRead([faxToMarkAsUnread], faxes);
    }

    public static async ensureFaxesAreMarkedAsRead(faxesToMarkAsUnread: InboundFax[], faxes: InboundFax[]): Promise<GenericResponse | null> {
        const faxIds = faxesToMarkAsUnread.filter(fax => !fax.ReadDate).map(fax => fax.Id);

        if (!faxIds.any())
            return null;

        const response = await this.actions.postAsync<GenericResponse>(Server.Myle_Faxing_MarkInboundFaxesAsRead, { FaxIds: faxIds });

        if (response.IsSuccess) {
            const updatedFaxes: InboundFax[] = faxes.map((fax: InboundFax) =>
                faxIds.some(faxIds => faxIds == fax.Id)
                    ? { ...fax, ReadDate: new Date() }
                    : fax
            );

            this.dispatchLocal({ faxes: updatedFaxes });
        }

        return response;
    }

    public static async saveFilterSettings(filteringSettings: InboundFaxMailboxFilterSettingsModel, isSaveSettingsChecked: boolean): Promise<void> {
        if (isSaveSettingsChecked)
            await this.saveUserSettings(filteringSettings);

        this.dispatchLocal({ filteringSettings }, DocumentUIState.InboundFaxInboxMailbox);
        this.dispatchLocal({ filteringSettings }, DocumentUIState.InboundFaxTrashMailbox);
        this.dispatchLocal({ filteringSettings }, DocumentUIState.InboundFaxIndexedMailbox);
    }

    public static async saveTriageFormSettings(settings: InboundFaxTriageFormSettingsModel): Promise<void> {
        await this.saveTriageFormUserSettings(settings);
    }

    public static async setCurrentFax(currentFax: InboundFax, allFaxes: InboundFax[]): Promise<void> {
        const faxes = [...allFaxes];
        const currentFaxIndex = faxes.findIndex(fax => fax.Id === currentFax.Id);
        if (currentFaxIndex != -1) {
            faxes[currentFaxIndex] = currentFax;
        }

        this.dispatchLocal({ currentFax, faxes });
    }

    public static showFaxIndexingForm(currentFax: InboundFax): void {
        this.dispatchLocal({ currentView: InboundFaxView.IndexingForm, currentFax });
    }

    public static showFaxTriageForm(currentFax: InboundFax): void {
        this.dispatchLocal({ currentView: InboundFaxView.TriageForm, currentFax });
    }

    public static showFaxMailbox(): void {
        this.dispatchLocal({ currentFax: null, currentView: InboundFaxView.FaxesList });
    }

    public static async createPatient(patientModel: IPatientCreationModel): Promise<CreatePatientResponse> {
        return await this.actions.postAsync<CreatePatientResponse>(
            Server.Myle_CreatePatient,
            PatientHelper.PrepareCreateNewPatientRequest(patientModel)
        );
    }

    public static async downloadInboundFax(fax: InboundFax): Promise<void> {
        const response = await this.actions.postAsync<DownloadInboundFaxResponse>(
            Server.Myle_Faxing_ForceDownloadInboundFax,
            { InboundFaxId: fax.Id }
        );

        fax.AttachmentId = response.AttachmentId;
    }

    public static async getInboundFaxPages(fax: InboundFax): Promise<LoadInboundFaxPagesResponse> {
        const response = await this.actions.postAsync<LoadInboundFaxPagesResponse>(
            Server.Myle_Faxing_GetInboundFaxPagesWithDetails,
            { FaxId: fax.Id }
        );

        response.InboundFaxPages = response
            .InboundFaxPages
            .map(pageDetails => ({ ...(new InboundFaxPageModel()), ...pageDetails }));

        return response;
    }

    public static async updateInboundFaxPages(inboundFaxPages: InboundFaxPageModel[]): Promise<GenericResponse> {
        const response = await this.actions.postAsync<GenericResponse>(
            Server.Myle_Faxing_UpdateInboundFaxPages,
            { inboundFaxPages }
        );

        return response;
    }

    public static setPdfViewerSelectedZoomLevelIndex(pdfViewerSelectedZoomLevelIndex: number): void {
        this.dispatchLocal({ pdfViewerSelectedZoomLevelIndex });
    }

    public static subtractOnePendingPage(faxes: InboundFax[], faxId: string): void {
        const selectedFaxIndex = faxes.findIndex(p => p.Id === faxId);
        if (selectedFaxIndex !== -1) {
            const updatedFaxes = faxes.map(p => p.Id === faxId ? { ...p, PendingPageCount: p.PendingPageCount - 1, DeletedPageCount: p.DeletedPageCount + 1 } : p);

            this.dispatchLocal({ faxes: updatedFaxes });
        }
    }

    public static async addFaxNote(faxId: Guid, note: string): Promise<GenericResponse> {
        return await this.actions.postAsync<GenericResponse>(
            Server.Myle_Faxing_AddNoteToInboundFax,
            { FaxId: faxId, Note: note }
        );
    }

    public static async updateInboundFaxAndSource(inboundFaxFormModel: InboundFaxIndexingFormModel): Promise<GenericResponse> {
        return await this.actions.postAsync<GenericResponse>(
            Server.Myle_Faxing_UpdateInboundFax,
            {
                FaxId: inboundFaxFormModel.id,
                IsUrgent: inboundFaxFormModel.isUrgent,
                ReportClassLookupId: inboundFaxFormModel.selectedClassLookupId,
                ReportSubClassLookupId: inboundFaxFormModel.selectedSubClassLookupId,
                FaxNote: inboundFaxFormModel.notes,
                ShouldAssignClassFaxNumber: inboundFaxFormModel.shouldAssignClassFaxNumber
            }
        );
    }

    public static updateFaxes(faxes: InboundFax[]): void {
        this.dispatchLocal({ faxes });
    }

    public static updateCurrentFax(currentFax: InboundFax): void {
        this.dispatchLocal({ currentFax });
    }

    public static saveCurrentForm(currentFaxesForm: { [id: string]: InboundFaxIndexingFormModel }, newFaxesForm: InboundFaxIndexingFormModel): void {
        const updatedFaxesForm: { [id: string]: InboundFaxIndexingFormModel } = JSON.parse(JSON.stringify(currentFaxesForm));
        const deepClonedNewFaxesForm: InboundFaxIndexingFormModel = JSON.parse(JSON.stringify(newFaxesForm));
        const selectedResponsibleProvidersWithType: Person[] = deepClonedNewFaxesForm?.selectedResponsibleProviders?.filter(p => p)?.map(p => new Person({ ...p })) ?? [];
        const selectedPatientWithType: Person = deepClonedNewFaxesForm?.selectedPatient ? new Person({ ...deepClonedNewFaxesForm.selectedPatient }) : null;

        updatedFaxesForm[newFaxesForm.id] = {
            ...new InboundFaxIndexingFormModel(),
            id: deepClonedNewFaxesForm.id,
            description: deepClonedNewFaxesForm.description,
            isUrgent: deepClonedNewFaxesForm.isUrgent,
            notes: deepClonedNewFaxesForm.notes,
            documentDate: deepClonedNewFaxesForm.documentDate,
            source: deepClonedNewFaxesForm.source,
            selectedClassLookupId: deepClonedNewFaxesForm.selectedClassLookupId,
            selectedSubClassLookupId: deepClonedNewFaxesForm.selectedSubClassLookupId,
            selectedSiteCode: deepClonedNewFaxesForm.selectedSiteCode,
            selectedResponsibleProviders: selectedResponsibleProvidersWithType,
            selectedPatient: selectedPatientWithType,
            shouldAssignClassFaxNumber: deepClonedNewFaxesForm.shouldAssignClassFaxNumber,
            shouldAssignSourceFaxNumber: deepClonedNewFaxesForm.shouldAssignSourceFaxNumber,
            isPatientFieldLocked: deepClonedNewFaxesForm.isPatientFieldLocked,
            isProvidersFieldLocked: deepClonedNewFaxesForm.isProvidersFieldLocked,
            isDescriptionFieldLocked: deepClonedNewFaxesForm.isDescriptionFieldLocked,
            isSiteFieldLocked: deepClonedNewFaxesForm.isSiteFieldLocked
        } as InboundFaxIndexingFormModel;

        this.dispatchLocal({ faxesForm: updatedFaxesForm }, DocumentUIState.InboundFaxInboxMailbox);
    }

    /**
     * Remove class and sub-class from inbound fax source by fax number
     * @param faxNumber
     * @returns
     */
    public static async dissociateClassFromFaxNumber(faxNumber: string): Promise<boolean> {
        const response = await this.actions.postAsync<GenericResponse>(
            Server.Myle_Faxing_DissociateClassAndSubClassFromInboundFaxSourceByFaxNumber,
            {
                FaxNumber: faxNumber
            }
        );

        if (response.Errors?.any()) {
            return false;
        }

        return true;
    }

    private static async saveUserSettings(filterSettings: InboundFaxMailboxFilterSettingsModel): Promise<void> {
        const userSettings = new UserSettings();
        userSettings.InboundFaxMailboxFilterSettings = filterSettings;

        const requestData = {
            UserSettings: userSettings,
            UserId: sessionStore.userId
        };

        const response = await this.actions.postAsync<GenericResponse>(Server.Myle_UpdateUserSettings, requestData);

        if (response?.IsSuccess) {
            cacheStore.UserSettings.InboundFaxMailboxFilterSettings = userSettings.InboundFaxMailboxFilterSettings;
        }
    }

    private static async saveTriageFormUserSettings(settings: InboundFaxTriageFormSettingsModel): Promise<void> {
        const userSettings = new UserSettings();
        userSettings.InboundFaxTriageFormSettings = settings;

        const requestData = {
            UserSettings: userSettings,
            UserId: sessionStore.userId
        };

        const response = await this.actions.postAsync<GenericResponse>(Server.Myle_UpdateUserSettings, requestData);

        if (response?.IsSuccess) {
            cacheStore.UserSettings.InboundFaxTriageFormSettings = userSettings.InboundFaxTriageFormSettings;
        }
    }

    private static async loadForPagination(mailboxModel: InboundFaxMailboxModel, mailboxType: InboundFaxMailboxType, pageOffset: number): Promise<InboundFax> {
        const response = await this.loadFaxes(
            {
                ...mailboxModel,
                queryCurrentPageNumber: mailboxModel.queryCurrentPageNumber + pageOffset
            },
            mailboxType
        );

        if (!response.IsSuccess)
            return null;

        this.setLoadInboundFaxesResponse(response);

        return pageOffset < 0
            ? response.Faxes[response.Faxes.length - 1]
            : response.Faxes.firstOrDefault();
    }

    private static async loadFaxes(mailboxModel: InboundFaxMailboxModel, mailboxType: InboundFaxMailboxType): Promise<LoadInboundFaxesResponse> {
        const requestData = {
            queryCurrentPageNumber: mailboxModel.queryCurrentPageNumber,
            queryOrderType: mailboxModel.queryOrderType,
            queryOrderDirection: mailboxModel.queryOrderDirection,
            searchText: mailboxModel.searchText,
            filteringSettings: mailboxModel.filteringSettings,
            mailboxType
        };

        return await this.actions.postAsync<LoadInboundFaxesResponse>(
            Server.Myle_Faxing_LoadInboundFaxes,
            requestData
        );
    }

    private static setLoadInboundFaxesResponse(response: LoadInboundFaxesResponse): void {
        InboundFaxesMailboxActions.dispatchLocal({
            faxes: response.Faxes,
            queryCurrentPageNumber: response.QueryCurrentPageNumber,
            queryPageCount: response.QueryPageCount,
            queryRowCount: response.QueryRowCount
        });
    }

    private static dispatchLocal(stateChanges: Partial<InboundFaxMailboxModel>, uiStateOverride: DocumentUIState | null = null): void {
        sessionStore.dispatch(LocalOperations.Documents.InboundFaxes_UpdateData, { stateChanges, uiStateOverride });
    }
}

export { InboundFaxesMailboxActions };
